/* Copyright (c) Trimble Inc. All rights reserved. */

import React from 'react';
import { useUserAvatarController } from './UserAvatar.controller';
import { UserAvatarView } from './UserAvatar.viewer';
import { withMVC } from '@tcp-base/design-system';
import './UserAvatar.scss';
import { UserAvatarModel, UserAvatarProps } from './UserAvatar.model';

/**
 * User Avatar Component (MVC Architecture with Live Sync)
 *
 * Displays user avatar using Model-View-Controller pattern with Zustand + Dexie live sync.
 *
 * Architecture:
 * - Store: useUserStore (Zustand + Dexie) - handles live user data with real-time IndexedDB sync
 * - Controller: UserAvatarController - contains all business logic and data processing
 * - View: UserAvatarView - pure presentation component with only JSX
 * - HOC: withMVC - connects Store, Controller, and View together
 *
 * Features:
 * - Live sync with IndexedDB using Dexie liveQuery
 * - Real-time updates when user data changes
 * - Displays user picture if available
 * - Falls back to initials if no picture
 * - Supports multiple sizes
 * - Optional display name
 * - Loading states during initial data fetch
 * - Clickable functionality
 * - Separation of concerns with MVC pattern
 */
export const UserAvatar: React.FC<UserAvatarProps> = withMVC<UserAvatarProps, UserAvatarModel>({
  useController: useUserAvatarController,
  Viewer: UserAvatarView,
});

export default UserAvatar;
